import { useQueryClient } from "@tanstack/react-query"
import type { Message } from "ably"
import type { ReactNode } from "react"
import { createContext, useContext } from "react"
import { useAuthStore } from "@/features/auth/store/authStore"
import type { Notification } from "@/features/notifications/services/notification.service"
import type { RealtimeConnectionStatus } from "@/hooks/useAblyRealtimeClient"
import { useRealtimeChannelMessages, useRealtimeConnection } from "./RealtimeContext"

interface NotificationRealtimeContextType {
  isConnected: boolean
  connectionStatus: RealtimeConnectionStatus
}

const NotificationRealtimeContext = createContext<NotificationRealtimeContextType>({
  isConnected: false,
  connectionStatus: "disconnected",
})

interface NotificationRealtimeProviderProps {
  children: ReactNode
}

type NotificationsInfiniteCache =
  | {
      pages: Array<{
        notifications: Notification[]
        total?: number
        page?: number
        pages?: number
      }>
      pageParams?: unknown[]
    }
  | undefined

export const NotificationRealtimeProvider = ({
  children,
}: NotificationRealtimeProviderProps): ReactNode => {
  const user = useAuthStore(state => state.user)
  const queryClient = useQueryClient()
  const { isConnected, connectionStatus } = useRealtimeConnection()

  const handleMessage = (message: Message) => {
    try {
      switch (message.name) {
        case "notification:new": {
          const notification = message.data as Notification

          queryClient.setQueryData(["notifications"], (old: unknown) => {
            const currentData = old as NotificationsInfiniteCache

            if (!currentData?.pages[0]) {
              return {
                pages: [
                  {
                    notifications: [notification],
                    total: 1,
                    page: 1,
                    pages: 1,
                  },
                ],
                pageParams: [1],
              }
            }

            return {
              ...currentData,
              pages: [
                {
                  ...currentData.pages[0],
                  notifications: [notification, ...currentData.pages[0].notifications],
                  total: (currentData.pages[0].total || 0) + 1,
                },
                ...currentData.pages.slice(1),
              ],
            }
          })

          queryClient.setQueryData(["unread-count"], (old: unknown) => {
            const currentCount = old as number | undefined
            return (currentCount || 0) + 1
          })
          break
        }
        case "notification:action_taken": {
          const data = message.data as {
            requestId: string
            actionTakenBy: string
            actionTakenByName: string
            action: "accepted" | "rejected"
          }

          queryClient.setQueryData(["notifications"], (old: unknown) => {
            const currentData = old as NotificationsInfiniteCache
            if (!currentData) return old

            return {
              ...currentData,
              pages: currentData.pages.map(page => ({
                ...page,
                notifications: page.notifications.map(notification =>
                  notification.entityId === data.requestId &&
                  notification.action === "join_request_received"
                    ? {
                        ...notification,
                        data: {
                          ...notification.data,
                          actionTakenBy: data.actionTakenBy,
                          actionTakenByName: data.actionTakenByName,
                          action: data.action,
                        },
                      }
                    : notification,
                ),
              })),
            }
          })
          break
        }
        case "notification:marked_read": {
          const data = message.data as {
            notificationId: string
            unreadCount: number
          }

          queryClient.setQueryData(["notifications"], (old: unknown) => {
            const currentData = old as NotificationsInfiniteCache
            if (!currentData) return old

            return {
              ...currentData,
              pages: currentData.pages.map(page => ({
                ...page,
                notifications: page.notifications.map(notification =>
                  notification.id === data.notificationId
                    ? { ...notification, isRead: true, readAt: new Date() }
                    : notification,
                ),
              })),
            }
          })
          queryClient.setQueryData(["unread-count"], data.unreadCount)
          break
        }
        case "notification:marked_all_read": {
          queryClient.setQueryData(["notifications"], (old: unknown) => {
            const currentData = old as NotificationsInfiniteCache
            if (!currentData) return old

            return {
              ...currentData,
              pages: currentData.pages.map(page => ({
                ...page,
                notifications: page.notifications.map(notification => ({
                  ...notification,
                  isRead: true,
                  readAt: new Date(),
                })),
              })),
            }
          })
          queryClient.setQueryData(["unread-count"], 0)
          break
        }
        case "notification:removed": {
          const data = message.data as {
            notificationId: string
            unreadCount: number
          }

          queryClient.setQueryData(["notifications"], (old: unknown) => {
            const currentData = old as NotificationsInfiniteCache
            if (!currentData) return old

            return {
              ...currentData,
              pages: currentData.pages.map((page, index) => ({
                ...page,
                notifications: page.notifications.filter(
                  notification => notification.id !== data.notificationId,
                ),
                total: index === 0 ? Math.max(0, (page.total || 0) - 1) : page.total,
              })),
            }
          })
          queryClient.setQueryData(["unread-count"], data.unreadCount)
          break
        }
        case "notification:unread_count_updated": {
          const data = message.data as { count: number }
          queryClient.setQueryData(["unread-count"], data.count)
          break
        }
        case "chat:unread_count_updated": {
          const data = message.data as { count: number }
          queryClient.setQueryData(["chat-unread-count"], data.count)
          break
        }
      }
    } catch (error) {
      console.error("Failed to process realtime notification message", {
        eventName: message.name,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  useRealtimeChannelMessages(user ? `user:${user.id}` : null, handleMessage)

  return (
    <NotificationRealtimeContext.Provider value={{ isConnected, connectionStatus }}>
      {children}
    </NotificationRealtimeContext.Provider>
  )
}

export const useNotificationRealtime = (): NotificationRealtimeContextType =>
  useContext(NotificationRealtimeContext)
